import { fetchDownloadUrl } from './fetchDownloadUrl';

/**
 * 첨부 다운로드/열기 URL 을 *클릭 시점에* 확보한다.
 *
 * 상세 응답의 `url` 은 서명 URL 이라 발급 후 짧은 시간(기본 180초) 뒤 만료된다.
 * 문서를 열어 둔 채 시간이 지나면 만료된 URL 로 요청이 나가고, 그 오류 응답이 브라우저에는
 * CORS 오류로 보인다(PORTAL-13977). `fileUuid` 가 있으면 클릭할 때마다 재발급받아
 * 이 문제를 없애고, 동시에 서버에 다운로드 로그도 남긴다.
 *
 * `fileUuid` 가 없는 첨부(구 인트라넷 FTP 업로드 등 파일 메타 미보유)는 만료가 없는 공개
 * URL 이므로 응답의 `url` 을 그대로 쓴다.
 *
 * 조회 자체는 `fetchDownloadUrl` 재사용. 그쪽은 빈 `url` 응답을 throw 로 끊는 정책이라
 * (상신 시퀀스에서 빈 링크가 실려 나가는 것을 막기 위함) 여기서는 그 throw 도 폴백으로 흡수한다
 * — 조회 화면은 링크가 하나 죽는 것보다 기존 URL 로라도 시도하는 편이 낫다.
 */
export const resolveDownloadUrl = async (
  fileUuid?: string | null,
  fallbackUrl?: string | null,
): Promise<string | undefined> => {
  const fallback = fallbackUrl || undefined;
  if (!fileUuid || fileUuid === 'uploading') return fallback;

  try {
    const { url } = await fetchDownloadUrl(fileUuid);
    return url;
  } catch {
    return fallback;
  }
};

/**
 * 현재 페이지 기준 절대 URL 로 변환.
 *
 * 구 인트라넷 첨부는 `//storage.fnf.co.kr/...` 처럼 스킴 없는(protocol-relative) URL 로 내려온다.
 * `<a href>` 는 문서 기준으로 알아서 해석하지만, 빈 창(`about:blank`)에 주입할 때는 기준 URL 이
 * 불분명해 브라우저마다 결과가 갈린다. 여기서 스킴을 명시적으로 붙여 그 차이를 없앤다.
 */
const toAbsoluteUrl = (url: string): string => {
  try {
    return new URL(url, window.location.href).toString();
  } catch {
    return url;
  }
};

/**
 * 첨부 새 창 열기.
 *
 * URL 재발급이 비동기라 `await` 뒤에 `window.open` 을 부르면 사용자 제스처가 끊겨 팝업이
 * 차단된다. 그래서 빈 창을 먼저 동기적으로 열어 두고, URL 이 도착하면 주입한다.
 * (`noopener` 를 feature 문자열로 주면 핸들이 null 이라 주입할 수 없으므로 `opener` 를 직접 끊는다.)
 */
export const openDownloadUrlInNewTab = async (
  fileUuid?: string | null,
  fallbackUrl?: string | null,
): Promise<void> => {
  const opened = window.open('', '_blank');
  if (opened) opened.opener = null;

  const url = await resolveDownloadUrl(fileUuid, fallbackUrl);
  if (!url) {
    opened?.close();
    return;
  }

  const href = toAbsoluteUrl(url);
  if (opened) opened.location.href = href;
  else window.open(href, '_blank', 'noopener,noreferrer');
};
